import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import NavbarTop from '../../components/NavbarTop';
import {
  fetchGamePixGames,
  GamePixGame,
} from '../../utils/gamepix';
import './style.css';

const mergeGames = (current: GamePixGame[], incoming: GamePixGame[]) => {
  const games = new Map(current.map(game => [game.namespace, game]));
  incoming.forEach(game => games.set(game.namespace, game));
  return Array.from(games.values());
};

const Games = () => {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const [games, setGames] = useState<GamePixGame[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadGames = useCallback(async (targetPage: number, replace = false) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const feed = await fetchGamePixGames(targetPage);
      if (requestId !== requestIdRef.current) return;

      setGames(current =>
        mergeGames(replace ? [] : current, feed.items),
      );
      setPage(targetPage);
      setHasMore(Boolean(feed.next_url) && feed.items.length > 0);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      console.error('[GamePix] Não foi possível carregar o catálogo.', loadError);
      setError('Não foi possível carregar os jogos. Verifique sua conexão e tente novamente.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGames(1, true);
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadGames]);

  const openGame = (game: GamePixGame) => {
    navigate(`/jogos/${encodeURIComponent(game.namespace)}`, {
      state: { title: game.title },
    });
  };

  return (
    <div className="games-page">
      <Helmet>
        <title>Paçoca - Jogos</title>
        <meta
          name="description"
          content="Jogue gratuitamente no Paçoca com um catálogo de jogos online."
        />
        <meta name="keywords" content="Paçoca, jogos, games, online" />
        <meta name="author" content="Paçoca Inc." />
        <meta property="og:title" content="Jogos do Paçoca" />
        <meta
          property="og:description"
          content="Jogue gratuitamente no Paçoca com um catálogo de jogos online."
        />
        <meta property="og:image" content="/img/link-preview.png?v=1" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <NavbarTop title="Paçoca Games" />

      <main className="games-content">
        <header className="games-heading">
          <div>
            <span className="games-eyebrow">JOGOS GRATUITOS</span>
            <h1>Escolha um jogo</h1>
            <p>Novos jogos são adicionados automaticamente ao catálogo.</p>
          </div>
        </header>

        {loading && games.length === 0 ? (
          <div className="games-status" role="status">
            <span className="games-spinner" aria-hidden="true" />
            <p>Carregando jogos...</p>
          </div>
        ) : null}

        {error && games.length === 0 ? (
          <div className="games-status games-status--error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => void loadGames(1, true)}>
              Tentar novamente
            </button>
          </div>
        ) : null}

        {games.length > 0 ? (
          <section className="games-grid" aria-label="Catálogo de jogos">
            {games.map(game => (
              <button
                key={game.namespace}
                className="game-card"
                type="button"
                onClick={() => openGame(game)}
                aria-label={`Jogar ${game.title}`}
              >
                <img
                  src={game.banner_image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <span className="game-card__content">
                  <strong>{game.title}</strong>
                  <small>{game.category?.replace(/-/g, ' ') || 'Jogo'}</small>
                </span>
              </button>
            ))}
          </section>
        ) : null}

        {games.length > 0 ? (
          <div className="games-footer">
            {error ? <p className="games-inline-error">{error}</p> : null}
            {hasMore ? (
              <button
                className="games-load-more"
                type="button"
                disabled={loading}
                onClick={() => void loadGames(page + 1)}
              >
                {loading ? 'Carregando...' : 'Carregar mais jogos'}
              </button>
            ) : (
              <p className="games-end">Você chegou ao fim do catálogo.</p>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default Games;
